import React, { useMemo, useState, useCallback } from "react";
import { MobileOutlined, TabletOutlined, LaptopOutlined, DesktopOutlined, MoreOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { typography, surfaces, borders, textColors } from "../../../design-system/tokens";
import InfoIcon from "../InfoIcon";

// Helper to read min/max from nested field metadata
function getCommonBounds(nestedFieldsMeta) {
  if (!nestedFieldsMeta) return { min: 1, max: 12 };
  const metas = Object.values(nestedFieldsMeta || {});
  let min = Infinity;
  let max = -Infinity;
  metas.forEach((m) => {
    const mmin = typeof m.appearance_min === "number" ? m.appearance_min : 1;
    const mmax = typeof m.appearance_max === "number" ? m.appearance_max : 12;
    min = Math.min(min, mmin);
    max = Math.max(max, mmax);
  });
  if (!isFinite(min)) min = 1;
  if (!isFinite(max) || max < min) max = 12;
  return { min, max };
}

function clamp(n, min, max) {
  if (typeof n !== "number" || isNaN(n)) return min;
  return Math.max(min, Math.min(max, n));
}

function deriveMobileDesktop(values, defaults) {
  const xs = typeof values?.extra_small === "number" ? values.extra_small : defaults[0];
  const s = typeof values?.small === "number" ? values.small : xs;
  const m = typeof values?.medium === "number" ? values.medium : defaults[2];
  const l = typeof values?.large === "number" ? values.large : m;
  const xl = typeof values?.extra_large === "number" ? values.extra_large : l;

  // Determine if two-mode is consistent (XS==S and M==L==XL)
  const isTwo = xs === s && m === l && l === xl;
  return {
    vMobile: xs, // use XS as representative
    vDesktop: m, // use M as representative
    isTwo,
  };
}

function RowControl({ icon, label, value, onChange, min, max }) {
  const dec = () => onChange(clamp((value || 0) - 1, min, max));
  const inc = () => onChange(clamp((value || 0) + 1, min, max));
  return (
    <div className="flex items-center justify-between w-full gap-2">
      <div className={`flex items-center gap-2 ${textColors.primary} ${typography.bodySmall}`}>
        {icon && <span>{icon}</span>}
        <span className="font-medium">{label}</span>
      </div>
      <div className="flex items-center gap-2">
        <button type="button" className={`px-2 py-1 rounded border ${borders.default} ${surfaces.primary} ${typography.caption}`} onClick={dec} aria-label={`decrease ${label}`}>
          <LeftOutlined />
        </button>
        <div className={`min-w-[2rem] text-center font-medium ${textColors.primary} select-none ${typography.bodySmall}`}>{value ?? ""}</div>
        <button type="button" className={`px-2 py-1 rounded border ${borders.default} ${surfaces.primary} ${typography.caption}`} onClick={inc} aria-label={`increase ${label}`}>
          <RightOutlined />
        </button>
      </div>
    </div>
  );
}

export default function ByLayoutIntegerField({ label, help, value, onChange, metadata, infoText }) {
  // Defaults used if values missing
  const DEFAULTS = [1, 2, 4, 5, 6];
  const nestedFieldsMeta = metadata?.nested_fields || {};
  const { min, max } = getCommonBounds(nestedFieldsMeta);

  const { vMobile, vDesktop, isTwo } = useMemo(() => deriveMobileDesktop(value, DEFAULTS), [value]);
  // UI mode: infer: One if all equal; else Two if grouped; else All.
  const allEqual = useMemo(() => {
    const xs = value?.extra_small;
    return (
      typeof xs === "number" &&
      ["small", "medium", "large", "extra_large"].every((k) => value?.[k] === xs)
    );
  }, [value]);
  const [mode, setMode] = useState(allEqual ? "one" : isTwo ? "two" : "all");

  // Apply mobile change to XS and S
  const applyMobile = useCallback(
    (newVal) => {
      onChange({ extra_small: clamp(newVal, min, max), small: clamp(newVal, min, max) });
    },
    [onChange, min, max]
  );

  // Apply desktop change to M/L/XL
  const applyDesktop = useCallback(
    (newVal) => {
      const v = clamp(newVal, min, max);
      onChange({ medium: v, large: v, extra_large: v });
    },
    [onChange, min, max]
  );

  // Collapse from All → Two: sync groups to XS and M respectively
  const collapseToTwo = useCallback(() => {
    const xs = typeof value?.extra_small === "number" ? value.extra_small : DEFAULTS[0];
    const m = typeof value?.medium === "number" ? value.medium : DEFAULTS[2];
    onChange({ extra_small: clamp(xs, min, max), small: clamp(xs, min, max), medium: clamp(m, min, max), large: clamp(m, min, max), extra_large: clamp(m, min, max) });
    setMode("two");
  }, [onChange, value, min, max]);

  const unifyAll = useCallback(() => {
    const xs = typeof value?.extra_small === "number" ? value.extra_small : DEFAULTS[0];
    const v = clamp(xs, min, max);
    onChange({ extra_small: v, small: v, medium: v, large: v, extra_large: v });
    setMode("one");
  }, [onChange, value, min, max]);

  // Expand to All – no mutation needed; just reveal controls
  const expandToAll = useCallback(() => setMode("all"), []);

  // Render
  return (
    <div className="field-wrapper">
      <div className="flex items-center justify-between mb-2">
        {label && (
          <label className={`block mb-1 ${typography.label} ${textColors.secondary}`}>
            {label}
            {infoText ? <span className="ml-2 align-middle"><InfoIcon text={infoText} /></span> : null}
          </label>
        )}
        <div className="flex gap-1">
          <button
            type="button"
            className={`px-2 py-1 rounded border ${mode === "one" ? `${surfaces.tertiary}` : `${surfaces.primary}`} ${borders.default} ${typography.caption}`}
            onClick={unifyAll}
            aria-label="Unified (One)"
            title="Unified (One)"
          >
            1
          </button>
          <button
            type="button"
            className={`px-2 py-1 rounded border ${mode === "two" ? `${surfaces.tertiary}` : `${surfaces.primary}`} ${borders.default} ${typography.caption}`}
            onClick={collapseToTwo}
            aria-label="Two groups"
            title="Two groups"
          >
            2
          </button>
          <button
            type="button"
            className={`px-2 py-1 rounded border ${mode === "all" ? `${surfaces.tertiary}` : `${surfaces.primary}`} ${borders.default} ${typography.caption}`}
            onClick={expandToAll}
            aria-label="Show all breakpoints"
            title="Show all breakpoints"
          >
            <MoreOutlined />
          </button>
        </div>
      </div>
      {help && <div className={`mb-2 ${typography.caption} ${textColors.tertiary}`}>{help}</div>}

      {mode === "one" ? (
        <div className="space-y-2">
          <RowControl icon={null} label="Unified" value={value?.extra_small ?? DEFAULTS[0]} onChange={(v) => onChange({ extra_small: clamp(v, min, max), small: clamp(v, min, max), medium: clamp(v, min, max), large: clamp(v, min, max), extra_large: clamp(v, min, max) })} min={min} max={max} />
        </div>
      ) : mode === "two" ? (
        <div className="space-y-2">
          <RowControl icon={<MobileOutlined />} label="Phone" value={vMobile} onChange={applyMobile} min={min} max={max} />
          <RowControl icon={<DesktopOutlined />} label="Desktop" value={vDesktop} onChange={applyDesktop} min={min} max={max} />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-2">
          <RowControl icon={<MobileOutlined />} label="Phone" value={value?.extra_small ?? DEFAULTS[0]} onChange={(v) => onChange({ extra_small: clamp(v, min, max) })} min={min} max={max} />
          <RowControl icon={<TabletOutlined />} label="Tablet" value={value?.small ?? (value?.extra_small ?? DEFAULTS[1])} onChange={(v) => onChange({ small: clamp(v, min, max) })} min={min} max={max} />
          <RowControl icon={<LaptopOutlined />} label="Laptop" value={value?.medium ?? DEFAULTS[2]} onChange={(v) => onChange({ medium: clamp(v, min, max) })} min={min} max={max} />
          <RowControl icon={<DesktopOutlined />} label="Desktop" value={value?.large ?? (value?.medium ?? DEFAULTS[3])} onChange={(v) => onChange({ large: clamp(v, min, max) })} min={min} max={max} />
          <RowControl icon={<DesktopOutlined />} label="Desk XL" value={value?.extra_large ?? (value?.large ?? DEFAULTS[4])} onChange={(v) => onChange({ extra_large: clamp(v, min, max) })} min={min} max={max} />
        </div>
      )}
    </div>
  );
}


